<template>
  <div class="w-full" v-loading="loading">
    <div class="pb-2">
      <div class="title">Time Sheet</div>

      <div class="table-scroll" :class="{ mobile: isMobileScreen }">
        <el-table :data="tableData" class="w-full">
          <el-table-column label="Loading" width="170">
            <template slot-scope="scope">
              <span class="asterisk">
                <span v-if="scope.$index < 3">*</span>
              </span>
              {{ scope.row.formDescription }}
            </template>
          </el-table-column>

          <el-table-column label="Date and Time" min-width="360">
            <template slot-scope="scope">
              <div class="flex">
                <div :class="{ 'time-error': !!timeErrors[scope.row && scope.row.orderIndex] }">
                  <el-date-picker v-model="scope.row.getTime" class="mr-2" prefix-icon="el-icon-date" size="mini"
                    type="datetime" :disabled="isDisabled || checkDisabled(scope.row)" :editable="false"
                    value-format="yyyy-MM-dd HH:mm:ss" format="yyyy-MM-dd HH:mm:ss" />
                </div>
                <el-button plain type="primary" size="mini" @click="fillCurrentTime(scope.row)"
                  :disabled="isDisabled || checkDisabled(scope.row)">
                  Now
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>
  </div>
</template>
<script>
import { pilotSaveOrUpdateTimesheet, selectFromById } from "@/services/form";
import dayjs from "dayjs";
import { mapState } from "vuex";
export default {
  name: "TimeSheet",
  props: {
    id: {
      type: Number
    },
    disabled: {
      type: Boolean,
      default: true,
    },
    nominationData: {
      type: Object
    },
  },
  data() {
    return {
      tableData: [],
      dataForm: {},
      loading: true,
      timeErrors: {},
    }
  },
  computed: {
    ...mapState(["screenType"]),
    isMobileScreen() {
      return this.screenType?.isMobileScreen ?? false;
    },
  },
  async created() {
    await this.getDetail()
    this.loading = false
  },
  mounted() {
    this.$emit('save', this.save)
  },
  methods: {
    normalizePickerString(v) {
      if (!v) return null

      const s =
        typeof v === 'string'
          ? v.replace('T', ' ').slice(0, 19)
          : v

      const d = dayjs(s)
      return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : null
    },

    isRowDisabled(row) {
      return this.isDisabled || this.checkDisabled(row)
    },

    async getDetail() {
      const res = await selectFromById({ nId: this.id, fId: 18 })

      this.dataForm = { ...res.data }

      this.dataForm.list = (this.dataForm.list || []).map(item => ({
        ...item,
        getTime: this.normalizePickerString(item.getTime),
      }))

      this.dataForm.list.forEach(item => {
        item.beforeAfterFlag = item.orderIndex <= this.beforeLength ? 0 : 1
      })

      this.dataForm.listItem =
        this.dataForm.listItem && this.dataForm.listItem.length > 0
          ? this.dataForm.listItem
          : [{}]

      this.tableData = this.dataForm.list.filter((item, index) => this.onlyFirst3Last3(index))
    },

    onlyFirst3Last3(index) {
      return index < 3 || index >= (this.dataForm.list.length - 3)
    },
    fillCurrentTime(row) {
      row.getTime = dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss')
    },
    checkDisabled(row) {
      const restricted = [
        "Pilot on board for sailing",
        "Started unmooring",
        "Vessel all clear"
      ];
      if (!restricted.includes(row.formDescription)) {
        return false;
      }
      // Unberth Without Operation: no operation happened, so open the last three
      // unberthing timings directly without waiting for "Documents on board".
      if (this.nominationData && this.nominationData.unberthWithoutOperation == 1) {
        return false;
      }
      const docRow = this.dataForm.list.find(item => item.formDescription === "Documents on board");
      if (!docRow || !docRow.getTime) {
        return true;
      }
      return false;
    },
    async save(extra = {}) {
      if (!this.isDisabled) {
        if (!this.validateSequence()) return

        this.loading = true
        try {
          const list = this.tableData.map(item => ({
            formId: item.formId,
            getTime: item.getTime
          }))

          const data = {
            nominationId: this.id,
            list,
            beforeAfterFlag: 1,
            ...(extra?.pilotModifyTimingRemark
              ? { pilotModifyTimingRemark: extra.pilotModifyTimingRemark }
              : {}),
          }

          await pilotSaveOrUpdateTimesheet(data)
          await this.getDetail()
          this.$message.success("Save Time Sheet Success")
        } finally {
          this.loading = false
        }
      }
    },

    parseTime(v) {
      if (!v) return null
      const d = dayjs(v)
      return d.isValid() ? d : null
    },

    getPickerOptions(index) {
      if (index <= 0) return {}

      const prev = this.parseTime(this.tableData[index - 1]?.getTime)

      if (!prev) return {}

      return {
        disabledDate: (time) => {
          return dayjs(time).endOf("day").isBefore(prev)
        }
      }
    },
    clearTimeErrors() {
      this.timeErrors = {}
    },

    hasTimeError(row) {
      return !!this.timeErrors?.[row?.orderIndex]
    },

    isRowDisabled(row) {
      return this.isDisabled || this.checkDisabled(row)
    },

    validateSequence() {
      this.timeErrors = {}

      for (let i = 1; i < this.tableData.length; i++) {
        const prevRow = this.tableData[i - 1]
        const curRow = this.tableData[i]

        if (this.isRowDisabled(prevRow) || this.isRowDisabled(curRow)) continue

        const prevRaw = prevRow?.getTime
        const curRaw = curRow?.getTime

        if (!prevRaw || !curRaw) continue

        const prev = dayjs(prevRaw)
        const cur = dayjs(curRaw)
        if (!prev.isValid() || !cur.isValid()) continue

        if (cur.isBefore(prev)) {
          this.$set(this.timeErrors, curRow.orderIndex, true)

          this.$message.error(
            `${curRow.formDescription} time cannot be earlier than ${prevRow.formDescription}`
          )
          return false
        }
      }
      return true
    },
  },
  computed: {
    isForPilot() {
      return this.nominationData.typeOfPilot == 'IHP'
    },
    isDisabled() {
      //disabled if not for pilot used
      return this.disabled || !this.isForPilot
    },
  }
}
</script>
<style lang="scss" scoped>
.title {
  font-family: 'Roboto';
  color: #1D1B20;
  font-size: 14px;
  font-weight: 800;
  margin-bottom: 10px;
}

.asterisk {
  color: red;
  display: inline-block;
  width: 5px;
}

::v-deep.el-table::before {
  height: 0px;
}

::v-deep.el-table thead tr {
  background-color: #F4F8FB;
}

::v-deep.el-table th.el-table__cell.is-leaf {
  background-color: transparent;
  font-size: 12px;
  font-weight: 800;
  color: #1D1B20;
}

::v-deep.el-table td.el-table__cell {
  font-size: 11px;
  font-weight: 600;
  color: #1D1B20;
  border: 1px solid #82889C;
  border-bottom: none;
}

::v-deep.el-table td.el-table__cell .cell {
  font-size: 11px;
  font-weight: 600;
  color: #1D1B20;
}

::v-deep.el-table td.el-table__cell:first-child {
  border-right: none;
}

::v-deep.el-table td.el-table__cell:last-child {
  border-left: none;
}

::v-deep.el-table tr:last-child td.el-table__cell {
  border-bottom: 1px solid #82889C;
}

::v-deep .time-error .el-input__inner {
  border-color: #F56C6C !important;
}

::v-deep .time-error .el-input__inner:focus {
  border-color: #F56C6C !important;
}

.table-scroll.mobile {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table-scroll.mobile ::v-deep .el-table {
  min-width: 520px;
}
</style>